import React, { useState, useRef, useMemo, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import { PauseIconVideo, PlayIconVideo } from '../../atoms/icons';
import { Image } from '../../atoms/image/Image';
import PlayerControls from './PlayerControls';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { cloudinaryUrl } from '../../../utils/cloudinary';

import styles from './CustomVideoControls.module.scss';

const CustomVideoControls = ({
  videoUrl,
  playIcon = () => <PlayIconVideo className={styles.videoIcons} />,
  pauseIcon = () => <PauseIconVideo className={styles.videoIcons} />,
  className,
  hideControls,
  theme = '',
  preview = false,
  overlayUrl,
  alt,
}) => {
  const serverSideRendering = useSelector((state) =>
    state.renderContext.serverSideRendering
  );
  const videoRef = useRef();
  const [isPlaying, setPlaying] = useState(true);
  const entry = useIntersectionObserver(videoRef, {}, serverSideRendering);
  const isVisible = !!entry?.isIntersecting;
  const isThumbs = theme === 'thumbs';

  useEffect(() => {
    if (isThumbs) return setPlaying(false);
  }, [theme]);

  const handlePlayPause = (e) => {
    e.stopPropagation();
    if (isThumbs) {
      return setPlaying(false);
    }
    setPlaying((prev) => !prev);
  };

  const isPlayingOnPlp = hideControls && isVisible;

  const formattedVideoUrl = useMemo(() => {
    return cloudinaryUrl({ quality: 'auto' }, 'video', videoUrl);
  }, [videoUrl]);

  const calculatePlayingStatus = () => {
    if (isThumbs) {
      return false;
    }

    return hideControls ? isPlayingOnPlp : isPlaying;
  };

  return (
    <div
      ref={videoRef}
      onClick={handlePlayPause}
      className={`${styles.playerWrapper} ${className}`}
    >
      {!serverSideRendering && (
        <ReactPlayer
          className={styles.player}
          url={formattedVideoUrl}
          controls={false}
          playing={calculatePlayingStatus()}
          playsinline={true}
          loop={true}
          muted={true}
          width="100%"
          height="100%"
          light={preview ? preview : false}
          onPlay={() => {
            if (isThumbs) return null;
            setPlaying(true);
          }}
          onPause={() => {
            setPlaying(false);
          }}
        />
      )}
      {!hideControls && (
        <PlayerControls
          playIcon={playIcon}
          pauseIcon={pauseIcon}
          onPlayPause={handlePlayPause}
          playing={isPlaying}
        />
      )}
      {overlayUrl && (
        <Image
          alt={alt}
          title={alt}
          itemProp="image"
          className={clsx(styles.overlayImage, className)}
          image={overlayUrl}
          opts={{ gravity: 'north_west' }}
        />
      )}
    </div>
  );
};

export default CustomVideoControls;
